/**
 * O resultado auditado na tela (E153.F3, ADR-0104 §2.6): lê ao montar, a cada `releitura` e a
 * pedido. A tela não decide nada: a situação, o que é consumível e a vigência de cada artefato vêm
 * do servidor.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';

import {
  ResultadoDoCasoError,
  lerResultadoDoCaso,
  type MotivoDaFalhaDoResultado,
  type ResultadoDoCasoData,
} from '@/lib/resultadoAuditado';

export type EstadoDoResultadoNaTela =
  | { estado: 'carregando' }
  | { estado: 'erro'; motivo: MotivoDaFalhaDoResultado }
  | { estado: 'pronto'; dados: ResultadoDoCasoData };

export interface UsoDoResultado {
  resultado: EstadoDoResultadoNaTela;
  reler: () => void;
}

export function useResultadoDoCaso(
  client: AxiosInstance,
  caseId: string,
  releitura?: unknown,
): UsoDoResultado {
  const [resultado, setResultado] = useState<EstadoDoResultadoNaTela>({ estado: 'carregando' });
  const [pedido, setPedido] = useState(0);
  const reler = useCallback(() => setPedido((n) => n + 1), []);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const dados = await lerResultadoDoCaso(client, caseId);
        if (vivo) setResultado({ estado: 'pronto', dados });
      } catch (err) {
        if (!vivo) return;
        const motivo = err instanceof ResultadoDoCasoError ? err.motivo : 'indisponivel';
        setResultado({ estado: 'erro', motivo });
      }
    })();
    return () => {
      vivo = false;
    };
  }, [client, caseId, releitura, pedido]);

  return { resultado, reler };
}
